import { useTranslation } from 'react-i18next';
import { Box, Group, Stack, Table, Text } from '@mantine/core';
import NoNds from '@/components/common/NoNds';
import { buildFactTable } from '@/lib/dealFact';
import { formatMoney, formatQualityValue, formatTons, headlineQuality } from '@/lib/format';
import type { BargainStepsInfo } from '@/types/deals';
import NumCell from '@/components/common/NumCell';
import numClasses from '@/components/common/NumCell.module.css';

interface DealFactTableProps {
  readonly info: BargainStepsInfo;
}

const numStyle = { whiteSpace: 'nowrap' as const };

// «Сделка по факту»: реально привезённый объём, фактическое качество и итог по факту (продавцу —
// выручка FCA, покупателю — затраты CPT; бэк отдаёт роль-зависимо). Зачёт «по дню» (строка на день)
// или «по среднему» (строка-период) — ветвление в buildFactRows. Все суммы БЕЗ НДС (конвенция
// платформы) — маркер у заголовка секции и итога; по ячейкам не дублируем (плотная таблица). Read-only.
export default function DealFactTable({ info }: DealFactTableProps) {
  const { t } = useTranslation();
  const { byDay, rows, totalNet, totalWeight } = buildFactTable(info);
  if (rows.length === 0) {
    return null;
  }

  const hq = headlineQuality(info.goodsProperties);
  const qualityLabel = hq?.name ?? t('app.deals.factDeal.colQuality');
  const qualityUnit = hq?.postfix ? ` ${hq.postfix}` : '';
  const quality = (v: number | undefined): string => (v != null ? `${formatQualityValue(v)}${qualityUnit}` : '—');
  const colDate = t(byDay ? 'app.deals.factDeal.colDay' : 'app.deals.factDeal.colPeriod');

  return (
    <Stack gap="sm">
      <Group gap={6} wrap="nowrap" align="center">
        <Text fz="sm" fw={600} c="gray.8">
          {t('app.deals.factDeal.fact')}
        </Text>
        <NoNds />
      </Group>

      {/* Десктоп — таблица */}
      <Box visibleFrom="sm">
        <Table withTableBorder verticalSpacing="sm" highlightOnHover highlightOnHoverColor="lime.0">
          <Table.Thead bg="brand.0">
            <Table.Tr>
              <Table.Th c="brand.9">{colDate}</Table.Th>
              <Table.Th c="brand.9" ta="right">
                {t('app.deals.factDeal.colTons')}
              </Table.Th>
              <Table.Th c="brand.9" ta="right">
                {qualityLabel}
              </Table.Th>
              <Table.Th c="brand.9" ta="right">
                {t('app.deals.factDeal.colPrice')}
              </Table.Th>
              <Table.Th c="brand.9" ta="right">
                {t('app.deals.factDeal.colAmount')}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.key}>
                <Table.Td style={numStyle}>{r.period}</Table.Td>
                <NumCell ta="right" style={numStyle}>{formatTons(r.tons)}</NumCell>
                <NumCell ta="right">{quality(r.quality)}</NumCell>
                <NumCell ta="right" style={numStyle}>{formatMoney(r.price)}</NumCell>
                <NumCell ta="right" fw={500} style={numStyle}>{formatMoney(r.amount)}</NumCell>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th>{t('app.deals.factDeal.total')}</Table.Th>
              <Table.Th ta="right" className={numClasses.num}>{formatTons(totalWeight)}</Table.Th>
              <Table.Th />
              <Table.Th />
              <Table.Th ta="right" className={numClasses.num}>{formatMoney(totalNet)} {t('app.deals.rub')}</Table.Th>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Box>

      {/* Мобайл — карточки (плотную таблицу не скроллим горизонтально). Единицы — в лейблах. */}
      <Stack gap="xs" hiddenFrom="sm">
        {rows.map((r) => (
          <Box key={r.key} p="sm" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
            <Text fz="sm" fw={600} mb={4}>
              {r.period}
            </Text>
            <FactKV label={t('app.deals.factDeal.colTons')} value={formatTons(r.tons)} />
            <FactKV label={qualityLabel} value={quality(r.quality)} />
            <FactKV label={t('app.deals.factDeal.colPrice')} value={formatMoney(r.price)} />
            <FactKV label={t('app.deals.factDeal.colAmount')} value={formatMoney(r.amount)} />
          </Box>
        ))}
        <Group justify="space-between" wrap="nowrap" px="sm">
          <Text fz="sm" fw={600}>
            {t('app.deals.factDeal.total')}
          </Text>
          <Text fz="sm" fw={600}>
            {formatTons(totalWeight)} {t('app.deals.tons')} · {formatMoney(totalNet)} {t('app.deals.rub')}
          </Text>
        </Group>
      </Stack>
    </Stack>
  );
}

function FactKV({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Group justify="space-between" gap="sm" wrap="nowrap">
      <Text fz="xs" c="gray.7">
        {label}
      </Text>
      <Text fz="sm">
        {value}
      </Text>
    </Group>
  );
}
